// Live Updates (Order Status V1) merchant config page copy. Canonical
// spelling is "Canceled" (one L) per the 7/15 product decision — the Figma's
// "Cancelled" is superseded.
export default {
  title: 'Live updates',
  // Page-level, so it frames Live Updates itself rather than the editor on
  // the Configuration tab (Sketch prototype live-updates-onboarding v31).
  subtitle:
    'Give your guests live order tracking — in your app, in your brand.',
  publish: 'Publish',
  loading: 'Loading live updates configuration',
  tabs: {
    aria_label: 'Live updates sections',
    overview: 'Overview',
    configuration: 'Configuration',
  },
  // Overview tab (FR-7/FR-36/FR-37 repositioning). Per the Sketch prototype
  // (live-updates-onboarding/live-updates-landing-for-developers-to-see v29),
  // the landing IS the education — there is no separate dismissible explainer;
  // the hero/why/how sections are the permanent, always-visible pitch, and
  // status/stack reflect this configuration's real connection.
  overview: {
    setup_disabled: {
      title: 'Live updates are off',
      body: "Nothing is being sent to guests, so there's no setup to preview.",
      turn_on: 'Turn on',
    },
    hero: {
      eyebrow: 'Digital experiences · Live updates',
      // `[[bold]]` marks the single fragment each sentence bolds via
      // withMarkers (ScheduledOrdersBanner precedent) — spliced from the
      // matching `_bold` key below.
      benefit_visibility:
        'Give guests [[bold]] into their order — from placed to delivered — with status pulled directly from your kitchen (and the delivery provider, when relevant).',
      benefit_visibility_bold: 'real-time visibility',
      benefit_push:
        'Just like on DoorDash/Uber or Delta/United, [[bold]] to their home screen with an active order… and clears on its own when complete.',
      benefit_push_bold: 'the Live Update pins your brand',
      benefit_control:
        '[[bold]] — update the customer every step of the production journey with real-time webhooks or time-based triggers… or keep it simple with “Order placed” and nothing more.',
      benefit_control_bold: 'As detailed as you want',
      published: {
        pill: 'Live updates published',
        benefit_visibility:
          'Guests can get [[bold]] into their order, updated at each step you’ve turned on, with status from your kitchen, your delivery provider, or a timer you set.',
        benefit_visibility_bold: 'real-time visibility',
        benefit_push:
          'Just like DoorDash/Uber or Delta/United, [[bold]] to their home screen while an order is active, and the Live Update clears on its own once the order is complete.',
        benefit_push_bold: 'your brand is pinned',
        benefit_control:
          '[[bold]]: choose which steps guests hear about for each configuration, using real-time webhooks or time-based triggers. Fine-tune it anytime in your configuration.',
        benefit_control_bold: 'As detailed as you want',
      },
      // Generic placeholder brand label in the decorative lock-screen mock —
      // deliberately not this merchant's real name (that's the live preview's
      // job, on the Configuration tab).
      la_brand: 'Your brand',
      // The lock screen's own date line, fixed alongside its fixed clock.
      la_date: 'Saturday, September 5',
      la_clock: '9:41',
      stages: {
        delivered: { sub: 'Enjoy — thanks for ordering', eta: '9:47pm' },
      },
    },
    why: {
      title: 'Why Live Updates matters to your customers',
      // The cycling brand-phone card (Sketch prototype
      // live-updates-onboarding v31, `.bigleagues`): the live activities a
      // guest already sees from big consumer brands. Illustrative sample
      // content — named brands, but no real data and no reproduced logos.
      big_leagues: {
        clock: '9:41',
        heading: 'Your app belongs in the big leagues',
        body: 'Locks to the homescreen during an active order, then clears on its own.',
        delivery: {
          name: 'DoorDash',
          meta: 'Arrives 9:49–9:59p',
          countdown: '10 min',
          countdown_suffix: 'until drop off',
        },
        boarding: {
          name: 'UA 565',
          meta: 'ABC123',
          seat: 'Group 2 · Seat 22D',
          route: 'SFO → LAX',
          headline: 'Boarding in 14 min',
          gate: 'Gate E12 · Terminal 3',
          status: 'On time',
        },
        flight: {
          name: 'DL82',
          status: 'On time',
          headline: 'Boarding at 2:55p',
          terminal: 'Int’l Terminal · Gate F8',
          depart: '3:40p ATL',
          arrive: 'CDG 6:15a',
        },
        courier: {
          name: 'Uber Eats',
          meta: 'now',
          headline: 'Heading your way',
          driver: 'Alex · Arrives 9:45–9:50p',
          call: 'Call',
          message: 'Message',
          tip: 'Tip',
        },
      },
      compare_title: 'Your brand’s own live-update',
      clock: '9:41',
      without_title: 'Without live updates',
      with_title: 'With Live Updates',
      // The "without" mini phone's pile of unrelated senders (Sketch
      // prototype live-updates-onboarding v29) — illustrative, not real data.
      without_notifications: {
        courier_name: 'Courier App',
        courier_time: '2m',
        courier_sub: 'Your driver is 4 stops away',
        sms_name: '555-0148',
        sms_time: '9m',
        sms_sub: 'Reply STOP to unsubscribe.',
        delivery_name: 'Delivery Partner',
        delivery_time: '14m',
        delivery_sub: 'We’ve picked up your order',
      },
      // The "with" mini phone's single clean card.
      with_card: {
        brand: 'Your brand',
        eta: 'ETA 9:44pm',
        status: 'Preparing your order',
        sub: 'The kitchen is on it',
      },
      compare_body:
        'One card instead of a pile of texts from senders they don’t recognize.',
    },
    how: {
      title: 'How it works',
      subtitle:
        'Three quick steps — most merchants finish in about five minutes.',
      step1_title: 'Choose the moments guests see',
      step1_body:
        'Pick which order stages appear — placed, preparing, ready, on the way.',
      step2_title: 'Write your messages',
      step2_body:
        'A short line in your brand voice for each stage — “We’re getting your order together.”',
      step3_title: 'Preview & publish',
      step3_body: 'See it mocked up on a phone screen.',
      // Copy for the self-playing mini-card demo under each step (Sketch
      // prototype live-updates-onboarding v29) — illustrative, not live data.
      demo: {
        brand: 'Your brand',
        eta: 'ETA 9:41pm',
        sub_placed: 'We’ve received your order',
        field_label: 'Live update text',
        message: 'We’re getting your order together.',
        publishing: 'Publishing…',
        published: 'Published',
      },
    },
    // The connection/status section: heading + subhead + action banner + stage
    // rail + provider stack, all derived from this configuration's real stack
    // and (for Olo) real webhook connection state — not a fixed marketing state.
    status: {
      // The rail previews the journey in stages, not the nine wire statuses:
      // "Delivery" stands for picked up / en route / arrived / delivered.
      stages: {
        placed: 'Order confirmed',
        preparing: 'Preparing',
        ready: 'Ready',
        delivery: 'Delivery',
        canceled: 'Order canceled',
      },
      // Qualifiers under a stage label.
      sublabels: {
        // Fires off the estimated ready time, not a provider event.
        estimated: 'Estimated',
        // The connection has gone quiet, so this stage may not reach guests.
        may_not_be_sending: 'May not be sending',
        // These locations deliver with their own drivers, so nothing reports
        // this stage to Thanx — it will never fire, however it's configured.
        not_available: 'Not available',
        // The Olo connection's states, captioned under each stage they gate.
        unlock_with_olo: 'Unlock with Olo',
        // The button's accessible name: Delivery and Canceled each carry one.
        unlock_with_olo_aria: 'Unlock with Olo: %{stage}',
        waiting_for_data: 'Waiting for data…',
      },
      // A guest is on one journey or the other, never both, so each gets its
      // own rail rather than being folded into a ladder nobody actually sees.
      // Canceled is its own track: either kind of order can end there.
      journeys: {
        pickup: 'Pickup orders',
        delivery: 'Delivery orders',
        canceled: 'Canceled orders',
      },
      preview_title: 'Setup preview',
      read_only_note: 'This is a read-only preview.',
      configure_cta: 'Configure setup',
      // The detected stack in one line under the picker; its pairs come from
      // the shared `live_updates.stack` labels.
      summary: {
        separator: '·',
      },
      olo: {
        not_configured_heading: 'Connect Olo to turn on live updates',
        not_configured_action_title:
          'Connect Olo webhooks to turn on live updates',
        not_configured_action_body:
          "Delivery and cancellation updates won't send until you connect Olo webhooks (~5 minutes).",
        not_configured_cta: 'Set up Olo webhooks',
        waiting_heading: 'Waiting for your first Olo event',
        waiting_action_title:
          'Connected Olo webhooks. Waiting for your first Olo event',
        waiting_action_body:
          "Waiting for data from your new webhook. This can take 1–2 hours. If it's been longer, confirm the webhook was published in Olo.",
        waiting_cta: 'Review setup',
        connected_heading: 'Olo webhooks connected',
        connected_sub:
          'Olo sends order and delivery events. Preparing runs on your estimated ready time (Olo has no preparing event); everything else is live.',
        connected_action_title: 'Olo webhooks connected',
        needs_attention_heading: 'Olo webhooks need attention',
        needs_attention_action_title: 'Olo webhooks need attention',
        // Must read exactly like the Configuration tab's
        // olo_webhook_banner.needs_reconnect_body.
        needs_attention_action_body:
          'Last received %{age}. Olo-triggered updates may not send until webhooks reconnect.',
        needs_attention_action_body_no_age:
          'Olo-triggered updates may not send until webhooks reconnect.',
        needs_attention_cta: 'Reconnect Olo webhooks',
      },
      flybuy: {
        heading: 'Live Updates is on — powered by Flybuy',
        action_title: 'Connected through Flybuy',
        action_body:
          'Flybuy sends live order and delivery events, so there is nothing to connect here.',
      },
      generic: {
        heading: 'Live Updates is on — via %{provider}',
        action_title: 'Connected — %{provider}',
        action_body: 'There is nothing to connect here.',
      },
    },
  },
  help: {
    // The "Help" heading is rendered by HelpCenterLink; this is the link text.
    link: 'How to set up live updates',
  },
  picker: {
    label: 'Choose a configuration to set up',
    // The Overview tab picks from the same list, but only to preview it.
    preview_label: 'Choose a configuration to preview',
    option: '%{label} (%{count} locations)',
    option_one_location: '%{label} (1 location)',
    view_locations: 'View locations',
    aria_label: 'Choose a configuration to set up',
    preview_aria_label: 'Choose a configuration to preview',
  },
  stack: {
    ordering: 'Ordering',
    delivery: 'Delivery',
    status_provider: 'Status provider',
  },
  shared_note:
    'Order Placed and Preparing stages are shared among pickup, delivery, and all other orders.',
  sections: {
    placed_preparation: 'Order placed and preparation',
    ready: 'Order ready notice',
    ready_note:
      'Order ready notice is not sent to consumers for delivery orders.',
    delivery: 'Delivery',
    canceled: 'Canceled orders',
  },
  statuses: {
    placed: 'Order placed',
    preparing: 'Preparing',
    ready: 'Order ready',
    picked_up: 'Order picked up by delivery driver',
    en_route: 'Order en route',
    arrived: 'Driver arrived at destination',
    delivered: 'Delivered',
    canceled_by_restaurant: 'Canceled by restaurant',
    canceled_by_delivery: 'Canceled by delivery',
  },
  card: {
    always_enabled: 'Always enabled',
    enable: 'Enable',
    disable: 'Disable',
    enable_aria: 'Enable %{status}',
    disable_aria: 'Disable %{status}',
    provider: 'Provider',
    message_type: 'Message type',
    channels: {
      push: 'Push',
      live_activity: 'Live Activity',
    },
    fields: {
      status_title: 'Status title',
      push_message: 'Push message',
      live_update_text: 'Live update text',
      characters_remaining_one: '%{count} character remaining',
      characters_remaining_other: '%{count} characters remaining',
      character_limit_reached: 'Character limit reached',
    },
    // Timer-mode trigger badge copy (Aaron QA 7/17): replaces the provider
    // label when trigger_mode === 'timer'. Uppercased by the badge's CSS.
    based_on_est_ready_time: 'Based on est. ready time',
    // Event-mode trigger badge copy, derived client-side rather than read from
    // the API's `trigger_source.label` (AI-1016) — see StatusCard's
    // `eventTriggerLabel`. Mirrors nucleus
    // StatusPresenter#event_trigger_label, including its
    // no-nameable-provider fallback. Uppercased by the badge's CSS.
    triggered_by: 'Triggered by %{provider}',
    triggered_by_generic: 'Triggered by this integration',
    managed_by_flybuy: 'Message managed by Flybuy',
    flybuy_push_note:
      "This push is sent automatically by Flybuy and can't be edited here.",
    signal: {
      question: 'Which signal do you want to use for this event?',
      use_provider_event: 'Use the %{provider} event',
      use_provider_event_generic: 'Use the provider event',
      base_on_ready_time: 'Base it off of estimated ready time',
      no_provider_event:
        "%{provider} doesn't send a %{event} event, so %{stage} always runs on your estimated ready time.",
      help: 'Trigger event accuracy is dependent on staff marking order state on the POS/KDS and that signal flowing through your ordering provider to Thanx. Using estimated ready time accuracy depends on properly configured make-time and kitchen capacity estimates from your ordering provider.',
      send_this_message: 'Send this message',
      minutes_one: '%{count} minute',
      minutes_other: '%{count} minutes',
      after_ready_time: "after provider's estimated ready time",
      before_ready_time: "before provider's estimated ready time",
      offset_aria_label: 'Timer offset in minutes',
      // Olo webhook connection status shown inside the "Use the Olo event"
      // card, which is only rendered while that option is unselectable — the
      // two unhealthy states of the three-state model. Once the connection is
      // healthy the card carries no status at all; `olo_webhook_banner` at the
      // top of the page is the single source of truth for connection health.
      olo_webhook: {
        // Natural-case; the badges uppercase via CSS text-transform, so screen
        // readers get the words rather than acronyms spelled out.
        not_connected_badge: 'Not connected',
        not_connected_note:
          'Connect Olo webhooks to turn on live order updates.',
        setup_link: 'Set up',
        waiting_badge: 'Waiting',
        waiting_note:
          "Waiting for data from your new webhook. This can take 1-2 hours. If it's been longer, confirm the webhook was published in Olo.",
        review_link: 'Review setup',
      },
    },
  },
  // Standing notice (not gated by any flag or wire field): live updates only
  // reach guests placing ASAP orders today, pending a separate scheduled-order
  // upgrade. Sits directly below the page-level OloWebhookBanner, above the
  // status sections (Figma 2838:23425). One sentence with two inline bold
  // runs — `[[scheduled_orders]]`/`[[asap_orders]]` mark where withBoldRuns
  // (ScheduledOrdersBanner.tsx) splices in `scheduled_orders`/`asap_orders`.
  scheduled_orders_banner: {
    body: 'Live updates for [[scheduled_orders]] are coming soon! Guests will only receive live updates from [[asap_orders]] for now.',
    scheduled_orders: 'scheduled orders',
    asap_orders: 'ASAP orders',
  },
  // order-status-push-disabled FF: lock-screen push + Live Activity delivery is switched off for this merchant.
  push_disabled: {
    banner_title: 'Push & live activity updates are disabled',
    notice_title: 'Your team disabled lock screen updates',
    body: 'Guests will not receive any push or live activity notifications',
    contact_link: 'Contact support to re-enable',
  },
  disabled_banner: {
    title: 'You turned off live updates',
    resume_link: 'Turn on live updates',
    illustration: {
      brand: 'PIZZA · ETA 9:41pm',
      title: 'Order placed',
      sub: 'We’ve received your order',
      placed: 'Placed',
      ready: 'Ready',
      delivered: 'Delivered',
    },
    live_updates: {
      body: 'Guests will not see order status or be asked to rate orders, at every location and in every configuration.',
      resume: '[[link]] to resume.',
    },
    order_feedback: {
      lead: 'No order feedback is being collected.',
      body: '[[lead]] Guests aren’t asked for feedback while live updates are off.',
      resume: '[[link]] to resume gathering feedback.',
    },
    order_ratings: {
      lead: 'No order ratings are being collected',
      body: '[[lead]], and the ratings report is paused.',
      resume: '[[link]] to resume order ratings.',
    },
    customer_profile: {
      body: 'No new order feedback is being collected.',
      resume: '[[link]] to resume gathering feedback.',
    },
    home_card: {
      body: 'No new order ratings are being collected.',
      resume: '[[link]] to resume order ratings.',
    },
  },
  order_feedback_tabs: {
    feedback_disabled:
      'Feedback collection is paused while live updates are off.',
    settings_disabled: 'Settings are locked while live updates are off.',
  },
  order_ratings_disabled: {
    heading: 'Digital order ratings',
    badge: 'Paused',
    title: "This report can't be loaded while live updates are off",
    saved_note: 'Your past ratings are saved. They are paused, not deleted.',
    body: "New order ratings stopped when you turned off live updates, so there's nothing to display here right now. Turn live updates back on to resume collection and reload the report.",
  },
  locked: {
    // FR-75 delivery lock card (Figma R4 936:4245): lock icon + title + body,
    // followed by the suppressed-message rows below.
    delivery_unavailable_title: "Delivery notifications aren't available",
    delivery_self_managed_body:
      "This configuration manages its own delivery, so Thanx doesn't receive delivery signals and can't send these updates. Pickup notifications above still work as normal.",
    delivery_disabled_body:
      "Delivery isn't enabled as a handoff mode for these locations, so Thanx won't send delivery updates.",
    // Suppressed-message row labels (Figma copy — intentionally shorter than
    // the configurable-card titles in `statuses.*`).
    suppressed: {
      picked_up: 'Order picked up by driver',
      en_route: 'Order en route',
      arrived: 'Driver arrived at destination',
      delivered: 'Delivered',
      canceled_by_restaurant: 'Canceled by restaurant',
      canceled_by_delivery: 'Canceled by delivery',
    },
    // FR-76 unsupported-ordering billboard (Figma R4 1000:4376): replaces the
    // entire status-section body when the configuration isn't configurable.
    unsupported_title: "Live updates aren't available for this configuration",
    unsupported_body:
      "This configuration uses an external ordering provider that doesn't send the order activity needed to power live updates. When this configuration moves to another ordering provider, live update settings will appear here.",
    unsupported_groups: {
      placed_preparation: 'Order placed and preparation',
      ready: 'Order ready notice',
      delivered: 'Delivered',
      canceled: 'Canceled orders',
    },
  },
  // Delivery + cancellation reach Thanx only over Olo webhooks, so until an
  // Olo event actually arrives neither section has a signal to configure —
  // each swaps its cards for this actionable card (Figma 2518:36887 /
  // 2536:40630). Both unhealthy states gate the sections, so each one has its
  // own title/body pair: "not connected" asks for setup, "waiting" explains
  // that the first event is still in flight.
  webhook_gate: {
    delivery_title: 'Connect Olo webhooks to turn on delivery updates',
    delivery_body:
      "Delivery updates can't be sent to guests because Olo webhooks are not yet set up.",
    canceled_title: 'Connect Olo webhooks to turn on cancellation updates',
    canceled_body:
      "Cancellation updates can't be sent to guests because Olo webhooks are not yet set up.",
    delivery_waiting_title:
      'Connected Olo webhooks. Waiting for your first Olo event',
    delivery_waiting_body:
      "Delivery updates can't be sent to guests because we're waiting for data from your new webhook. This can take 1-2 hours. If it's been longer, confirm the webhook was published in Olo.",
    canceled_waiting_title:
      'Connected Olo webhooks. Waiting for your first Olo event',
    canceled_waiting_body:
      "Cancellation updates can't be sent to guests because we're waiting for data from your new webhook. This can take 1-2 hours. If it's been longer, confirm the webhook was published in Olo.",
    set_up: 'Set up',
    review_setup: 'Review setup',
    // Both cards can render at once, so the shared "Set up" / "Review setup"
    // labels need accessible names that say which section they belong to.
    set_up_delivery_aria: 'Set up Olo webhooks for delivery updates',
    set_up_canceled_aria: 'Set up Olo webhooks for cancellation updates',
    review_setup_delivery_aria: 'Review Olo webhook setup for delivery updates',
    review_setup_canceled_aria:
      'Review Olo webhook setup for cancellation updates',
  },
  preview_disabled: {
    title: 'You turned off live updates',
    body: 'Guests will not see order status or be asked to rate orders.',
    turn_on: 'Turn on',
  },
  preview: {
    push_live_activity: 'Push & Live Activity',
    in_app: 'In-App',
    preview_as_you_edit: 'Preview updates as you edit',
    ios: 'Preview iOS',
    android: 'Preview Android',
    order_status: 'Order status',
    order_number: 'Order #468458-987',
    eta: 'ETA 9:41pm',
    // In-app order-status summary card ETA (Figma 368:7674).
    in_app_eta: 'ETA: 10:45pm',
    your_items: 'Your items',
    store_location: 'Store location',
    // Flybuy pickup check-in affordance (Figma 577:7432) — mock chrome shown
    // in the in-app tracker for Flybuy status-provider stacks only.
    check_in: 'Check in',
    // Accessible names for the segmented tab pair and platform toggle group.
    tabs_label: 'Preview mode',
    platform_toggle_label: 'Preview platform',
    // Static device-mock chrome — a design mock clock, not a live time.
    status_date: 'Thu Mar 26',
    ios_time: '9:41',
    android_time: '9:30',
    // role="group" accessible names for each device frame.
    ios_frame_label: 'iOS lock screen preview',
    android_frame_label: 'Android lock screen preview',
    in_app_frame_label: 'In-app order status preview',
    // FR-76 locked preview overlay (Figma R4 1000:4376) — shown over the
    // 20%-opacity phone mock when the configuration is fully locked.
    locked_overlay: "Live updates aren't available",
    // Both the lock-screen Live Activity and in-app tracker require a minimum
    // guest app version to render at all — a permanent client capability, not
    // a merchant-configurable state (Figma 2844:28382).
    // Kept short (and rendered small) so it stays on one line in the narrow
    // preview column.
    app_version_notice:
      'Only guests on app v58.0.0 or later see the live updates',
    tracker: {
      placed: 'Placed',
      preparing: 'Preparing',
      ready: 'Ready',
      on_the_way: 'On the way',
      delivered: 'Delivered',
    },
    // Default in-app stage-row labels. The row matching the focused status shows
    // the merchant's edited title instead; the rest show these static defaults.
    in_app_stages: {
      placed: 'Order placed',
      preparing: 'Preparing your order',
      ready: 'Ready for pickup',
      on_the_way: 'On the way',
      delivered: 'Delivered',
    },
    // Fixed sample line items, shared by the in-app tracker mock and the
    // Overview hero's order-review screen (never merchant-driven). Named after
    // things any merchant plausibly sells, so the mock reads as a real order
    // rather than a schema.
    items: {
      item_a_name: 'Bottled Water',
      item_a_option: '20 oz',
      item_a_price: '$2.49',
      item_b_name: 'Diet Coke',
      item_b_price: '$1.99',
    },
  },
  // Page-level Olo webhook banner — the primary surface for connection health,
  // sitting above the status sections. Three states: no secret saved, secret
  // saved but no event yet, and healthy. A connected-but-silent-for-24h
  // connection is not a fourth state: `connectionState` stays 'connected',
  // but the banner swaps to the `needs_reconnect_*` copy/icon/colors below.
  olo_webhook_banner: {
    not_connected_title: 'Connect Olo webhooks to turn on live updates',
    not_connected_body:
      "Delivery and cancellation updates won't send until you connect Olo webhooks (~5 minutes).",
    not_connected_link: 'Set up Olo webhooks',
    waiting_title: 'Connected Olo webhooks. Waiting for your first Olo event',
    waiting_body:
      "Waiting for data from your new webhook. This can take 1-2 hours. If it's been longer, confirm the webhook was published in Olo.",
    waiting_link: 'Review setup',
    connected_title: 'Olo webhooks connected',
    connected_body: 'Last received %{age}',
    // Webhook status only syncs every 30 minutes, so a precise "X minutes
    // ago" for anything under an hour reads as more accurate than it is.
    connected_body_recent: 'Received within the last hour',
    // Fallback for the (state-machine-unreachable, but defensively handled)
    // case of a connected merchant with no usable timestamp.
    connected_body_no_timestamp: 'Receiving Olo events',
    reconnect_link: 'Reconnect Olo webhooks',
    // Stale-for-24h copy (Figma 2733:23890): swaps title/body/icon/colors on
    // top of the CTA swap above, so a merchant scanning the page notices the
    // connection needs attention rather than reading it as still healthy.
    needs_reconnect_title: 'Olo webhooks need attention',
    needs_reconnect_body:
      'Last received %{age}. Olo-triggered updates may not send until webhooks reconnect.',
  },
  // "Connect Olo webhooks" setup/rotate dialog (Figma 1354:33049). Doubles as
  // the first-time setup flow and the later secret-rotation flow.
  olo_webhook_modal: {
    title: 'Connect Olo webhooks',
    subtitle:
      'Live updates need Olo to send order events to Thanx. (~5 minutes)',
    // Natural case; the eyebrows uppercase via CSS text-transform, so screen
    // readers get the words rather than the letters spelled out.
    url_section_label: '1. Give Olo this URL',
    secret_section_label: '2. Paste Olo’s secret here',
    steps: {
      // `%{inline}` marks the fragment each step renders as its own element —
      // a link on step one, a bolded field name on step three.
      one: 'In Olo, go to %{inline} → Integrations → Webhooks.',
      one_inline: 'my.olo.com',
      two: 'Create a webhook and choose Thanx as the partner. Delete any existing Thanx webhook first.',
      three: 'Paste this into Olo’s %{inline} field:',
      three_inline: 'Destination URL',
      four: 'Enable the Thanx event set.',
      five: 'Copy the secret key and paste it below.',
      six: 'In Olo, click Test webhook, then Publish.',
    },
    webhook_url_label: 'Your Thanx webhook URL',
    webhook_url_note:
      'This URL is unique to your account, keep the event set as “Thanx”.',
    copy_url: 'Copy',
    url_copied: 'Webhook URL copied to clipboard',
    url_copy_failed:
      "We couldn't copy the webhook URL. Please copy it manually.",
    setup_guide_link: 'See the full setup guide',
    secret_label: 'Olo webhook secret',
    secret_placeholder: 'Paste the secret key from Olo',
    submit: 'Save',
    save_error: "We couldn't save your webhook secret. Please try again.",
    // Success state shown in place of the setup steps once the secret saves
    // (Figma 1298:31799) — the connection only turns green once Olo actually
    // sends an event, so the copy sets that expectation rather than claiming
    // the webhook is live.
    saved_title: 'Key saved',
    saved_body:
      'Your key has been saved. As new orders flow through Olo, your live updates will start sending. This can take a little while to show up here.',
    saved_confirm: 'Done',
  },
  actions_menu: {
    aria_label: 'Live updates actions',
    turn_off: 'Turn off live updates',
  },
  header: {
    turn_on: 'Turn on live updates',
  },
  turn_off_modal: {
    title: 'Turn off Live updates?',
    body: "Turns off live updates for your whole brand, every configuration and every location, effective immediately. Here's what stops:",
    danger_zone: 'Danger zone',
    lock_screen_title: 'No lock-screen updates',
    lock_screen_body:
      'Your customers will not see any reference from your app about their order.',
    in_app_title: 'No in-app order updates',
    in_app_body:
      'The in-app order tracker turns off — guests see the classic receipt only.',
    feedback_title: 'No order ratings/feedback (or access to RecoveryAI).',
    feedback_body: 'No new data in Order ratings; Order feedback goes quiet.',
    confirm: 'Turn off live updates',
    cancel: 'Cancel',
    error: 'Live updates could not be turned off. Try again.',
    error_recovery_ai:
      'Recovery AI could not be turned off, so live updates are still on. Contact support if this keeps happening.',
    success:
      'You turned off live updates for your brand. Order feedback and ratings are paused, and RecoveryAI is off.',
  },
  discard_turn_off_modal: {
    title: 'Discard your edits and turn off?',
    body: 'Your unpublished edits will be lost, and live updates stay off.',
    cancel: 'Cancel',
    confirm: 'Discard',
  },
  publish_modal: {
    title: 'Publish changes to live updates',
    body: "These changes go live for guests placing orders at the affected locations. Existing in-progress orders aren't affected.",
    body_turn_on:
      "Publishing turns live updates back on for your whole brand. Existing in-progress orders aren't affected.",
    confirm: 'Publish changes',
    cancel: 'Keep editing',
  },
  unsaved_modal: {
    title: 'You have unpublished changes',
    body: "Unsaved changes won't be shown to customers until you publish. What would you like to do?",
    discard: 'Discard changes',
    keep_editing: 'Keep editing',
    publish: 'Publish changes',
  },
  locations_modal: {
    title: '%{label} locations',
    count_one: '1 location',
    count_other: '%{count} locations',
    search_placeholder: 'Search locations',
    search_aria_label: 'Search locations by name, street, city, or zip',
    column: 'Location',
    close: 'Close',
    empty: 'No locations match your search',
    error: 'We had trouble loading locations. Please try again.',
  },
  conflict: {
    message:
      'This configuration has changed since you loaded it. Reload to pick up the latest published version, then reapply your edits.',
    reload: 'Reload configuration',
  },
  errors: {
    load_failed: 'We had trouble loading your live updates configuration.',
    publish_failed: 'We had trouble publishing your changes. Please try again.',
    retry: 'Retry',
    // The sections and preview slots on the Configuration tab can both show
    // an unavailable-detail retry notice at once — distinct accessible
    // names so a screen-reader user tabbing through can tell them apart
    // (mirrors webhook_gate's set_up_delivery_aria/set_up_canceled_aria for
    // the same two-similar-CTAs-on-one-page shape).
    retry_sections_aria: 'Retry loading status sections',
    retry_preview_aria: 'Retry loading the live preview',
    retry_setup_preview_aria: 'Retry loading the setup preview',
  },
  empty: {
    title: 'No configurations to set up yet',
    note: "Live updates is enabled for your account, but there aren't any location groups to configure yet. This usually resolves once your locations finish syncing — check back shortly, or contact support if it persists.",
  },
  publish_success: 'Your changes are live.',
  dev_tools: {
    title: 'Live Updates dev tools',
    stack_label: 'Stack',
    stack_aria_label: 'Dev tools: jump to a stack permutation',
    olo_webhook_label: 'Olo webhook',
    olo_webhook_aria_label: 'Dev tools: Olo webhook signal state',
    olo_webhook_not_applicable: 'Not applicable to this stack',
    hide_aria_label: 'Hide the Live Updates dev tools until the next reload',
  },
}
